import { MediaClip } from "../types";

/**
 * Generate filter counts for the SideFilterSheet component based on actual media clips data
 * @param mediaClips Array of media clips from your API/data source
 * @returns Counts object ready to use with SideFilterSheet
 */
export function generateMediaClipCounts(mediaClips: MediaClip[]): Record<string, Record<string, number>> {
  // Initialize the structure for our categories
  const counts: Record<string, Record<string, number>> = {
    brand: {},
    sentiment: {},
    category: {},
    source: {}
  };

  // Process each media clip
  mediaClips.forEach(clip => {
    // Count brands
    if (clip.brand) {
      const brand = clip.brand;
      counts.brand[brand] = (counts.brand[brand] || 0) + 1;
    }

    // Count sentiments
    if (clip.sentiment) {
      const sentiment = clip.sentiment;
      counts.sentiment[sentiment] = (counts.sentiment[sentiment] || 0) + 1;
    }

    // Count categories
    if (clip.category) {
      const category = clip.category;
      counts.category[category] = (counts.category[category] || 0) + 1;
    }

    // Count sources
    if (clip.source) {
      const source = clip.source;
      counts.source[source] = (counts.source[source] || 0) + 1;
    }
  });

  return counts;
}

/**
 * Example usage in your data-fetching component:
 * 
 * // In your component that fetches media clips
 * import { generateMediaClipCounts } from "../utils/filterCountsGenerator";
 * 
 * // After fetching media clips
 * const [mediaClips, setMediaClips] = useState<MediaClip[]>([]);
 * const [filterCounts, setFilterCounts] = useState<Record<string, Record<string, number>>>({});
 * 
 * useEffect(() => {
 *   const fetchData = async () => {
 *     const data = await fetchMediaClips();
 *     setMediaClips(data);
 *     
 *     // Generate counts from the actual data
 *     const counts = generateMediaClipCounts(data);
 *     setFilterCounts(counts);
 *   };
 *   
 *   fetchData();
 * }, []);
 * 
 * // Then in your JSX:
 * <SideFilterSheet
 *   // other props...
 *   optionCounts={filterCounts}
 * />
 */

/**
 * Generic function to count any array of objects by specific properties
 * @param items Array of any objects
 * @param categoryFieldMap Map of category names to property names in the objects
 * @returns Counts object ready to use with SideFilterSheet
 */
export function generateFilterCounts<T extends Record<string, any>>(
  items: T[],
  categoryFieldMap: Record<string, keyof T>
): Record<string, Record<string, number>> {
  const counts: Record<string, Record<string, number>> = {};
  
  // Initialize categories
  Object.keys(categoryFieldMap).forEach(category => {
    counts[category] = {};
  });
  
  // Count items
  items.forEach(item => {
    Object.entries(categoryFieldMap).forEach(([category, field]) => {
      const value = item[field];
      
      if (value === undefined || value === null || value === '') {
        // Skip undefined/null/empty values
        return;
      }
      
      if (Array.isArray(value)) {
        // Handle array values (if a property can have multiple values)
        value.forEach((val: any) => {
          const strVal = String(val);
          counts[category][strVal] = (counts[category][strVal] || 0) + 1;
        });
      } else {
        // Handle single value
        const strVal = String(value);
        counts[category][strVal] = (counts[category][strVal] || 0) + 1;
      }
    });
  });
  
  return counts;
} 